import React, { useState, useEffect, useCallback, useRef } from 'react';
import { extractApiError } from '../../utils/apiError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faSave,
  faCube,
  faRuler,
  faWeight,
  faTags,
  faBox,
  faWarehouse,
  faTrashAlt,
  faLayerGroup,
  faPlus,
  faTrash,
  faBalanceScale,
  faIndianRupeeSign,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { kgPerMetre, formatKg, formatMetres } from '../../utils/coilMath';
import CoilEntrySheet from '../coils/CoilEntrySheet';
import productCategoryService from '../../services/productCategoryService';
import productCatalogueService from '../../services/productCatalogueService';
import warehouseService from '../../services/warehouseService';
import { useCoilSettings } from '../../context/CoilSettingsContext';
import { useTranslation } from 'react-i18next';
import { validateProductSizeForm } from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';

/**
 * ProductSizeForm Component
 * Modal form for adding/editing product sizes with validation
 */
const ProductSizeForm = ({
  isOpen,
  onClose,
  onSubmit,
  productSize = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  // String constants for UI text
  const strings = t('productSizes.forms', { returnObjects: true }) || {};
  const requiredIndicator = t('common.symbols.required');
  // Per-size coil tracking only exists while the global switch is on — offering it
  // for a disabled feature would just be a checkbox that does nothing.
  const { coilTrackingEnabled } = useCoilSettings();

  const [formData, setFormData] = useState({
    alias_name: '',
    product_size: '',
    product_weight: '',
    standard_length: '',
    product_thickness: '',
    thickness_unit: '',
    // Per-SKU price (migration 061). Optional — blank = unpriced.
    price: '',
    current_stock: '0',
    minimum_stock: '0',
    product_category_id: '',
    product_catalogue_id: '',
    coil_tracked: false,
    track_by_pieces: false,
    warehouses: [],
  });
  
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  // Which warehouse's coils the entry drawer is currently capturing (null = closed).
  const [coilSheetWarehouseId, setCoilSheetWarehouseId] = useState(null);

  const [errors, setErrors] = useState({});
  // Local submit state — the form owns its button lifecycle (reset in finally)
  // and only closes on genuine success, so a failed save no longer discards data.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [categories, setCategories] = useState([]);
  const [productCatalogues, setProductCatalogues] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [catalogueSearchLoading, setCatalogueSearchLoading] = useState(false);
  const catalogueSearchRef = useRef(null);

  // Static cache to persist data across component instances
  if (!window.productSizeFormCache) {
    window.productSizeFormCache = {
      categories: null,
      productCatalogues: null,
      categoriesLoaded: false,
      productCataloguesLoaded: false,
      loading: false
    };
  }
  const cache = window.productSizeFormCache;

  // Initialize form data when productSize prop changes
  useEffect(() => {
    // guard to prevent async updates after effect is stale
    let isActive = true;

    if (productSize) {
      setFormData({
        alias_name: productSize.alias_name || '',
        product_size: productSize.product_size || '',
        product_weight: productSize.product_weight ? productSize.product_weight.toString() : '',
        standard_length: productSize.standard_length ? productSize.standard_length.toString() : '',
        product_thickness: productSize.product_thickness || '',
        thickness_unit: productSize.thickness_unit || '',
        // price (migration 061): prefer the SKU's own price, fall back to the
        // derived base_price; blank when the SKU is unpriced.
        price: (productSize.price ?? productSize.base_price) != null
          ? String(productSize.price ?? productSize.base_price)
          : '',
        current_stock: productSize.current_stock ? productSize.current_stock.toString() : '0',
        minimum_stock: productSize.minimum_stock ? productSize.minimum_stock.toString() : '0',
        product_category_id: productSize.product_category_id ? productSize.product_category_id.toString() : '',
        product_catalogue_id: productSize.product_catalogue_id ? productSize.product_catalogue_id.toString() : '',
        coil_tracked: !!productSize.coil_tracked,
        track_by_pieces: !!productSize.track_by_pieces,
        warehouses: Array.isArray(productSize.warehouses) ? productSize.warehouses.map(w => ({
          warehouse_id: w.warehouse_id,
          stock_quantity: w.stock_quantity || 0,
          warehouse_name: w.warehouse_name || '',
          // Already linked on the server. Its BALANCE is not editable here — changing a
          // live balance belongs to the warehouse-inventory screens, and the API ignores
          // stock_quantity for a warehouse the size is already in. Coils, though, can be
          // ADDED: the server credits them onto the existing stock.
          existing: true,
          // Only coils captured during THIS edit. A size's already-entered coils live in
          // the coil ledger; listing them here would invite editing stock history.
          coils: [],
        })) : [],
      });

      // capture ids as strings to avoid type mismatch
      const capturedCategoryId = productSize.product_category_id ? String(productSize.product_category_id) : null;
      const capturedProductId = productSize.product_catalogue_id ? String(productSize.product_catalogue_id) : null;

      // helper: inject or fetch category display name
      const ensureCategoryOption = async () => {
        if (!capturedCategoryId) return;

        // prefer name fields from payload
        let name = productSize.category_name || productSize.name || productSize.product_category_name || productSize.categoryName || productSize.category || '';
        // fallback: fetch by id if no name present
        if (!name) {
          try {
            setLoadingOptions(true);
            const res = await productCategoryService.getProductCategoryById(capturedCategoryId).catch(() => null);
            if (!isActive) return;
            const data = res && (res.data || res);
            name = data && (data.name || data.category_name || data.product_category_name || data.categoryName || data.title || '');
          } catch (e) {
            // ignore fetch failure
          } finally {
            if (isActive) setLoadingOptions(false);
          }
        }

        if (!name) return;

        const newCategory = { id: capturedCategoryId, name };
        if (!isActive) return;
        // inject at top if missing
        setCategories(prev => {
          const exists = prev.some(cat => String(cat.id) === String(capturedCategoryId));
          if (exists) return prev;
          return [newCategory, ...prev];
        });

        cache.categories = cache.categories || [];
        if (!cache.categories.some(c => String(c.id) === String(capturedCategoryId))) {
          cache.categories = [newCategory, ...cache.categories];
        }
      };

      // helper: inject or fetch product display name
      const ensureProductOption = async () => {
        if (!capturedProductId) return;

        let name = productSize.product_name || productSize.name || productSize.alias_name || productSize.product_catalogue_name || productSize.productName || '';
        if (!name) {
          try {
            setLoadingOptions(true);
            const res = await productCatalogueService.getProductCatalogueById(capturedProductId).catch(() => null);
            if (!isActive) return;
            const data = res && (res.data || res);
            name = data && (data.product_name || data.name || data.alias_name || data.title || '');
          } catch (e) {
            // ignore
          } finally {
            if (isActive) setLoadingOptions(false);
          }
        }

        if (!name) return;

        const newProduct = { id: capturedProductId, product_name: name, name };
        if (!isActive) return;
        setProductCatalogues(prev => {
          const exists = prev.some(p => String(p.id) === String(capturedProductId));
          if (exists) return prev;
          return [newProduct, ...prev];
        });

        cache.productCatalogues = cache.productCatalogues || [];
        if (!cache.productCatalogues.some(p => String(p.id) === String(capturedProductId))) {
          cache.productCatalogues = [newProduct, ...cache.productCatalogues];
        }
      };

      // fire both (don't await to keep UI snappy)
      void ensureCategoryOption();
      void ensureProductOption();
    } else {
      setFormData({
        alias_name: '',
        product_size: '',
        product_weight: '',
        standard_length: '',
        product_thickness: '',
        thickness_unit: '',
        price: '',
        current_stock: '0',
        minimum_stock: '0',
        product_category_id: '',
        product_catalogue_id: '',
        coil_tracked: false,
        track_by_pieces: false,
        warehouses: []
      });
    }

    setErrors({});
    setCoilSheetWarehouseId(null);

    return () => {
      // prevent state updates from stale async work
      isActive = false;
    };
  }, [productSize, isOpen, cache]);

  const loadDropdownOptions = useCallback(async () => {
    // debug logs removed

    // Check if we already have cached data - avoid API calls if data exists
    if (cache.categoriesLoaded && cache.productCataloguesLoaded) {
      if (categories.length === 0 && cache.categories) {
        setCategories(cache.categories);
      }
      if (productCatalogues.length === 0 && cache.productCatalogues) {
        setProductCatalogues(cache.productCatalogues);
      }
      return;
    }

    // Prevent multiple simultaneous calls
    if (cache.loading) {
      return;
    }
    
    cache.loading = true;
    setLoadingOptions(true);
    
    try {
      const needCategories = !cache.categoriesLoaded;
      const needProductCatalogues = !cache.productCataloguesLoaded;

  // debug logs removed

      const promises = [];
      
      if (needCategories) {
        // Use productCategoryService to fetch categories
        promises.push(
          productCategoryService.getProductCategories()
            .catch(() => ({ success: false, data: [] }))
        );
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.categories }));
      }

      // HSN codes removed - skip

      if (needProductCatalogues) {
        // Use productCatalogueService to fetch product catalogues
        promises.push(
          productCatalogueService.getAllProducts()
            .catch(() => ({ success: false, data: [] }))
        );
      } else {
        promises.push(Promise.resolve({ success: true, data: cache.productCatalogues }));
      }

  const [categoriesResult, productCataloguesResult] = await Promise.all(promises);

      // Helper to normalize different API shapes: plain array or { success, data }
      const extractArray = (res) => {
        if (!res) return [];
        // If API returned the array directly
        if (Array.isArray(res)) return res;
        // If API returned { data: [...] }
        if (res.data && Array.isArray(res.data)) return res.data;
        // If API returned { result: [...] }
        if (res.result && Array.isArray(res.result)) return res.result;
        // If API returned { success: true, data: [...] }
        if (res.success && res.data && Array.isArray(res.data)) return res.data;
        // Fallback to empty
        return [];
      };

      const categoriesData = extractArray(categoriesResult);
      const productCataloguesData = extractArray(productCataloguesResult);

    // debug logs removed

      if (categoriesData.length > 0) {
        // Merge fetched data with any existing items (e.g., injected from edit mode)
        setCategories(prev => {
          const merged = [...categoriesData];
          prev.forEach(existing => {
            if (!merged.some(cat => cat.id === existing.id)) {
              merged.push(existing);
            }
          });
          return merged;
        });
        cache.categories = categoriesData;
        cache.categoriesLoaded = true;
      }

      // HSN removed

      if (productCataloguesData.length > 0) {
        // Merge fetched data with any existing items (e.g., injected from edit mode)
        setProductCatalogues(prev => {
          const merged = [...productCataloguesData];
          prev.forEach(existing => {
            if (!merged.some(prod => prod.id === existing.id)) {
              merged.push(existing);
            }
          });
          return merged;
        });
        cache.productCatalogues = productCataloguesData;
        cache.productCataloguesLoaded = true;
      } else {
        // Still mark as loaded even if empty to avoid infinite loops
        cache.productCataloguesLoaded = true;
        cache.productCatalogues = [];
      }
    } catch (error) {
      // Reset loading flags on error to allow retry
      cache.loading = false;
      setLoadingOptions(false);
    } finally {
      cache.loading = false;
      setLoadingOptions(false);
    }
  }, [ cache,categories,productCatalogues]);


  // Load dropdown options when form opens, but use cached data when available
  useEffect(() => {
    if (isOpen) {
      // First check if we have cached data and populate local state
      if (cache.categories && categories.length === 0) {
        setCategories(cache.categories);
      }
      if (cache.productCatalogues && productCatalogues.length === 0) {
        setProductCatalogues(cache.productCatalogues);
      }

      // Only load if we don't have cached data and not already loading
      if ((!cache.categoriesLoaded || !cache.productCataloguesLoaded) && !cache.loading) {
        loadDropdownOptions();
      }
    }
  }, [
    isOpen,
    loadDropdownOptions,
    cache.categories,
    cache.categoriesLoaded,
    cache.productCatalogues,
    cache.productCataloguesLoaded,
    cache.loading,
    productCatalogues.length,
    categories.length,
  ]);

  // Fetch warehouses
  useEffect(() => {
    if (isOpen) {
      const fetchW = async () => {
        setLoadingWarehouses(true);
        try {
          const res = await warehouseService.getAllWarehouses();
          if (res.success) {
            setAvailableWarehouses(Array.isArray(res.data) ? res.data : []);
          }
        } catch (e) {
          console.error('Error fetching warehouses:', e);
        } finally {
          setLoadingWarehouses(false);
        }
      };
      fetchW();
    }
  }, [isOpen]);

  const handleAddWarehouse = (warehouseId) => {
    if (!warehouseId) return;
    const warehouse = availableWarehouses.find(w => String(w.id) === String(warehouseId));
    if (!warehouse) return;

    if (formData.warehouses.some(w => String(w.warehouse_id) === String(warehouseId))) {
      return; // Already added
    }

    setFormData(prev => ({
      ...prev,
      warehouses: [...prev.warehouses, {
        warehouse_id: parseInt(warehouseId),
        // On a coil-tracked size the quantity is computed from the coils and the input is
        // read-only, so it must START at 0. Left blank it would fail the "stock quantity
        // is required" check with no editable field for the operator to fix it in.
        stock_quantity: prev.coil_tracked ? 0 : '',
        warehouse_name: warehouse.name,
        // Newly linked in this save, so its opening balance IS ours to set.
        existing: false,
        coils: []
      }]
    }));
  };

  const handleRemoveWarehouse = (warehouseId) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.filter(w => String(w.warehouse_id) !== String(warehouseId))
    }));
  };

  const handleWarehouseStockChange = (warehouseId, quantity) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.map(w =>
        String(w.warehouse_id) === String(warehouseId)
          ? { ...w, stock_quantity: quantity === '' ? '' : parseFloat(quantity) || 0 }
          : w
      )
    }));
  };

  // ---------------------------------------------------------------------------
  // Opening stock as individual coils
  //
  // A coil-tracked size has no stock quantity anyone can type: its balance IS the sum of
  // its coils, and each coil carries its OWN weight/length ratio — kg/m is a property of
  // the physical roll, never derived from the product master. So the warehouse's Stock
  // Quantity is computed from the coils listed under it and shown read-only; the totals
  // match by construction rather than by a validation the operator can fail.
  //
  // Capture is delegated to CoilEntrySheet — the same drawer purchase unloading uses —
  // so there is one coil editor, one set of rules and one row shape in the app.
  // ---------------------------------------------------------------------------

  const coilsFor = (warehouse) => (Array.isArray(warehouse?.coils) ? warehouse.coils : []);

  /** A warehouse's opening stock: its coils' weights, whole kg, added up. */
  const coilWeightTotal = (warehouse) => coilsFor(warehouse).reduce((sum, coil) => {
    const weight = parseFloat(coil?.weight_kg);
    return Number.isFinite(weight) ? sum + Math.round(weight) : sum;
  }, 0);

  /** Drop a warehouse's coil error once its coils have changed. */
  const clearCoilError = (warehouseId) => {
    const index = formData.warehouses.findIndex(w => String(w.warehouse_id) === String(warehouseId));
    if (index < 0) return;
    setErrors(prev => {
      const key = `warehouses.${index}.coils`;
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /**
   * Rows handed back by the drawer. They are already validated against the API's rules
   * (whole kg, positive length, batch present and unique) by the shared validator the
   * sheet runs before it closes.
   */
  const handleCoilsSaved = (warehouseId, coils) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.map(w => (
        String(w.warehouse_id) === String(warehouseId)
          ? { ...w, stock_quantity: 0, coils }
          : w
      ))
    }));
    clearCoilError(warehouseId);
  };

  /**
   * Removing the last coil is allowed and leaves the warehouse at zero: opening stock can
   * genuinely be nothing, and the size still needs to exist in that warehouse.
   */
  const handleRemoveCoil = (warehouseId, coilIndex) => {
    setFormData(prev => ({
      ...prev,
      warehouses: prev.warehouses.map(w => (
        String(w.warehouse_id) === String(warehouseId)
          ? { ...w, coils: coilsFor(w).filter((_, index) => index !== coilIndex) }
          : w
      ))
    }));
    clearCoilError(warehouseId);
  };

  // When options load, if productSize provides a name but formData doesn't have a matching id,
  // try to find the option by name and set the id so the controlled <select> can display it.
  useEffect(() => {
    if (!productSize) return;

    // Sync category by name -> id if needed
    try {
      if (categories && categories.length > 0) {
        const hasSelectedId = formData.product_category_id && categories.some(c => String(c.id) === String(formData.product_category_id));
        if (!hasSelectedId && productSize.category_name) {
          const target = String(productSize.category_name || '').trim().toLowerCase();
          const match = categories.find(c => String((c.name || c.category_name || '')).trim().toLowerCase() === target);
          if (match) {
            setFormData(prev => ({ ...prev, product_category_id: String(match.id) }));
          }
        }
      }

      // Sync product catalogue by name -> id if needed
      if (productCatalogues && productCatalogues.length > 0) {
        const hasSelectedProdId = formData.product_catalogue_id && productCatalogues.some(p => String(p.id) === String(formData.product_catalogue_id));
        if (!hasSelectedProdId && productSize.product_name) {
          const targetP = String(productSize.product_name || '').trim().toLowerCase();
          const matchP = productCatalogues.find(p => String((p.product_name || p.name || p.alias_name || '')).trim().toLowerCase() === targetP);
          if (matchP) {
            setFormData(prev => ({ ...prev, product_catalogue_id: String(matchP.id) }));
          }
        }
      }
    } catch (e) {
      // swallow any errors from matching
    }
  }, [formData.product_catalogue_id, formData.product_category_id,categories, productCatalogues,productSize]);

  const handleCatalogueSearch = useCallback((term) => {
    if (catalogueSearchRef.current) clearTimeout(catalogueSearchRef.current);
    catalogueSearchRef.current = setTimeout(async () => {
      setCatalogueSearchLoading(true);
      try {
        const result = await productCatalogueService.getProductCatalogue({ search: term, limit: 20, skip: 0 });
        if (result.success && Array.isArray(result.data)) {
          setProductCatalogues(prev => {
            const merged = [...result.data];
            // keep currently selected item if not in results
            if (formData.product_catalogue_id) {
              const exists = merged.some(p => String(p.id) === String(formData.product_catalogue_id));
              if (!exists) {
                const current = prev.find(p => String(p.id) === String(formData.product_catalogue_id));
                if (current) merged.unshift(current);
              }
            }
            return merged;
          });
        }
      } catch (e) {
        // ignore
      } finally {
        setCatalogueSearchLoading(false);
      }
    }, 400);
  }, [formData.product_catalogue_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Handle numeric fields
    if (['product_weight', 'standard_length', 'current_stock', 'minimum_stock', 'price'].includes(name)) {
      newValue = value.replace(/[^0-9.]/g, '');
      const parts = newValue.split('.');
      if (parts.length > 2) {
        newValue = parts[0] + '.' + parts.slice(1).join('');
      }
      // Limit decimal places based on field
      if (['product_weight', 'standard_length'].includes(name) && parts[1] && parts[1].length > 3) {
        newValue = parts[0] + '.' + parts[1].substring(0, 3);
      } else if (name === 'price' && parts[1] && parts[1].length > 2) {
        // Price is 2-dp (matches NUMERIC(10,2) on the SKU).
        newValue = parts[0] + '.' + parts[1].substring(0, 2);
      } else if (['current_stock', 'minimum_stock'].includes(name) && parts[1]) {
        // Stock fields typically don't need decimals
        newValue = parts[0];
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = validateProductSizeForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit
    if (!validateForm()) return;

    const payload = {
      alias_name: formData.alias_name.trim(),
      product_size: formData.product_size.trim(),
      product_weight: formData.product_weight.trim() ? parseFloat(formData.product_weight) : null,
      standard_length: formData.standard_length.trim() ? parseFloat(formData.standard_length) : null,
      product_thickness: formData.product_thickness.trim() || null,
      thickness_unit: formData.thickness_unit.trim() || null,
      // Optional per-SKU price (migration 061). Blank -> null (unpriced).
      price: String(formData.price).trim() !== '' ? parseFloat(formData.price) : null,
      product_category_id: parseInt(formData.product_category_id),
      product_catalogue_id: parseInt(formData.product_catalogue_id),
      coil_tracked: !!formData.coil_tracked,
      track_by_pieces: !!formData.track_by_pieces,
      // A warehouse the size is ALREADY in and that gained no coils in this edit is a
      // no-op server-side (stock_quantity is ignored for existing links), so it is left
      // out entirely rather than sent as an entry that looks like it does something.
      warehouses: formData.warehouses
        .filter(w => !w.existing || coilsFor(w).length > 0)
        .map(w => {
        if (!formData.coil_tracked) {
          // Coils may only be sent on a coil-tracked size — the server refuses them
          // otherwise — so anything captured and then unticked stays out of the payload.
          return {
            warehouse_id: w.warehouse_id,
            stock_quantity: parseFloat(w.stock_quantity) || 0
          };
        }

        // stock_quantity is ALWAYS 0 here. The server credits the aggregate balance from
        // each coil as it creates it, so sending a quantity as well would either double
        // the stock or be refused outright by the opening-stock guard.
        const entry = { warehouse_id: w.warehouse_id, stock_quantity: 0 };

        const coils = coilsFor(w);
        if (coils.length > 0) {
          entry.coils = coils.map(coil => ({
            batch_number: String(coil.batch_number).trim(),
            weight_kg: Math.round(parseFloat(coil.weight_kg)),
            length_m: parseFloat(coil.length_m),
            colour: String(coil.colour || '').trim() || null,
            supplier_batch_ref: String(coil.supplier_batch_ref || '').trim() || null,
            received_date: coil.received_date || null,
          }));
        }
        // No coils: the key is omitted entirely rather than sent as [], because zero
        // opening stock is a legitimate state, not an empty list to be validated.

        return entry;
      })
    };

    setSubmitError('');
    setSubmitting(true);
    try {
      const result = await onSubmit(payload);
      // Only close on genuine success. Previously onClose() was in `finally`,
      // so a failed save silently closed the modal and discarded the entry.
      if (result && result.success) {
        onClose();
      } else {
        const rawErr = result && result.error;
        const msg = typeof rawErr === 'string' ? rawErr : (rawErr ? extractApiError(rawErr) : '');
        setSubmitError(msg || 'Could not save. Please try again.');
      }
    } catch (err) {
      setSubmitError(extractApiError(err) || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  

  if (!isOpen) return null;

  // The warehouse whose coils the drawer is capturing, and the header line that tells the
  // operator which size and which warehouse they are typing into.
  const coilSheetWarehouse = formData.warehouses.find(
    w => String(w.warehouse_id) === String(coilSheetWarehouseId)
  ) || null;
  const coilSheetProduct = (formData.alias_name || '').trim() || (formData.product_size || '').trim();
  const coilSheetWarehouseName = coilSheetWarehouse
    ? (coilSheetWarehouse.warehouse_name || `#${coilSheetWarehouse.warehouse_id}`)
    : '';
  const coilSheetTitle = coilSheetProduct
    ? t('coils.productSize.openingCoils.sheetTitle', {
        product: coilSheetProduct,
        warehouse: coilSheetWarehouseName,
      })
    : coilSheetWarehouseName;

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.product-size-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .product-size-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto product-size-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >

        <div 
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2 
            className="text-2xl font-bold"
            style={{ color: UI_COLORS.TEXT.PRIMARY }}
          >
            {productSize ? strings.title?.edit : strings.title?.add}
          </h2>
          {/* Debug button - remove in production */}
          <div className="flex items-center space-x-2">
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ 
                color: UI_COLORS.TEXT.MUTED,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>
        </div>


        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Alias Name */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faCube} className="mr-2" />
              {`${strings.fields?.alias_name?.label} ${requiredIndicator}`}
            </label>
            <input
              type="text"
              name="alias_name"
              value={formData.alias_name}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.alias_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.alias_name && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields?.alias_name?.placeholder}
            />
              {errors.alias_name && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.alias_name}
              </p>
            )}
          </div>

          {/* Product Size */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faRuler} className="mr-2" />
              {`${strings.fields?.product_size?.label} ${requiredIndicator}`}
            </label>
            <input
              type="text"
              name="product_size"
              value={formData.product_size}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.product_size ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.product_size && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields?.product_size?.placeholder}
            />
            {errors.product_size && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_size}
              </p>
            )}
          </div>

          {/* Weight and Standard Length Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faWeight} className="mr-2" />
                {strings.fields?.product_weight?.label}
              </label>
              <input
                type="number"
                name="product_weight"
                value={formData.product_weight}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.product_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.product_weight && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.product_weight?.placeholder}
                min="0"
                step="0.001"
              />
              {errors.product_weight && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.product_weight}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faRuler} className="mr-2" />
                {strings.fields?.standard_length?.label}
              </label>
              <input
                type="number"
                name="standard_length"
                value={formData.standard_length}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.standard_length ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.standard_length && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.standard_length?.placeholder}
                min="0"
                step="0.001"
              />
              {errors.standard_length && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.standard_length}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faRuler} className="mr-2" />
                {strings.fields?.product_thickness?.label}
              </label>
              <input
                type="text"
                name="product_thickness"
                value={formData.product_thickness}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.product_thickness ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.product_thickness && {
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                  })
                }}
                placeholder={strings.fields?.product_thickness?.placeholder}
              />
              {errors.product_thickness && (
                <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {errors.product_thickness}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label 
                className="block text-sm font-semibold"
                style={{ color: UI_COLORS.FORMS.LABEL }}
              >
                <FontAwesomeIcon icon={faTags} className="mr-2" />
                {strings.fields?.thickness_unit?.label}
              </label>
              <input
                type="text"
                name="thickness_unit"
                value={formData.thickness_unit}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }}
                placeholder={strings.fields?.thickness_unit?.placeholder}
              />
            </div>
          </div>

          {/* Price — per-SKU master price (migration 061). Optional: leaving it
              blank keeps the SKU unpriced. Order/inquiry/PO forms auto-fill a
              line's price from this when the SKU is picked. */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faIndianRupeeSign} className="mr-2" />
              {strings.fields?.price?.label || 'Price'}
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              onWheel={(e) => e.target.blur()}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.price ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.price && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
              placeholder={strings.fields?.price?.placeholder || 'Optional — leave blank if unpriced'}
              min="0"
              step="0.01"
            />
            {errors.price && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.price}
              </p>
            )}
          </div>

          {coilTrackingEnabled && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="coil_tracked"
                  checked={!!formData.coil_tracked}
                  disabled={!!formData.track_by_pieces}
                  onChange={(e) => {
                    const on = e.target.checked;
                    // Zero any stock already typed. Otherwise the number stays in state,
                    // the read-only input merely hides it, and the save is refused by a
                    // value the operator can no longer see or edit.
                    //
                    // Coils already captured are KEPT when the box is unticked — an
                    // accidental click must not throw away hand-typed rows — and simply
                    // not sent, because the server refuses coils on a non-coil size.
                    setFormData(prev => ({
                      ...prev,
                      coil_tracked: on,
                      // Mutually exclusive with pieces-tracking: coil stock IS the
                      // kg sum of its coils, while track_by_pieces reinterprets the
                      // same balance as a piece count. A size carrying both renders
                      // a kg figure labelled "(pcs)" and has no coherent stock unit.
                      track_by_pieces: on ? false : prev.track_by_pieces,
                      warehouses: on
                        ? (prev.warehouses || []).map(w => ({
                            ...w,
                            stock_quantity: 0,
                            coils: Array.isArray(w.coils) ? w.coils : [],
                          }))
                        : prev.warehouses,
                    }));
                    setCoilSheetWarehouseId(null);
                  }}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                />
                <span className="text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
                  {t('coils.productSize.coilTracked.label')}
                </span>
              </label>
              <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {t('coils.productSize.coilTracked.help')}
              </p>
            </div>
          )}

          {/* Track inventory by pieces instead of kg. Always available (no global
              switch): the flag just reinterprets the unit of this size's stock. */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="track_by_pieces"
                checked={!!formData.track_by_pieces}
                disabled={!!formData.coil_tracked}
                onChange={(e) => {
                  const on = e.target.checked;
                  // See the coil checkbox above — a size is one or the other.
                  setFormData(prev => ({
                    ...prev,
                    track_by_pieces: on,
                    coil_tracked: on ? false : prev.coil_tracked,
                  }));
                  if (on) setCoilSheetWarehouseId(null);
                }}
                className="h-4 w-4 rounded"
                style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
              />
              <span className="text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faBox} className="mr-2" />
                {t('pieces.productSize.trackByPieces.label')}
              </span>
            </label>
            <p className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {t('pieces.productSize.trackByPieces.help')}
            </p>
            {productSize && (!!productSize.track_by_pieces !== !!formData.track_by_pieces) && (
              <p className="text-xs font-medium" style={{ color: UI_COLORS.STATUS?.WARNING || '#b45309' }}>
                <FontAwesomeIcon icon={faBalanceScale} className="mr-1.5" />
                {t('pieces.productSize.trackByPieces.flipWarning')}
              </p>
            )}
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faTags} className="mr-2" />
              {`${strings.fields?.category_id?.label} ${requiredIndicator}`}
            </label>
            <select
              name="product_category_id"
              value={formData.product_category_id}
              onChange={handleChange}
              disabled={loadingOptions}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.product_category_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                ...(errors.product_category_id && {
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                })
              }}
            >
              <option value="">{strings.fields?.category_id?.placeholder}</option>
              {categories.map(category => {
                return (
                  <option
                    key={category.id}
                    value={category.id}
                  >
                    {category.name || category.category_name}
                  </option>
                );
              })}
            </select>
            {errors.product_category_id && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_category_id}
              </p>
            )}
          </div>

          {/* Product Catalogue Selection */}
          <div className="space-y-2">
            <label
              className="block text-sm font-semibold"
              style={{ color: UI_COLORS.FORMS.LABEL }}
            >
              <FontAwesomeIcon icon={faBox} className="mr-2" />
              {`${strings.fields?.product_catalogue_id?.label} ${requiredIndicator}`}
            </label>
            <SearchableSelect
              name="product_catalogue_id"
              value={formData.product_catalogue_id}
              onChange={handleChange}
              disabled={loadingOptions}
              loading={catalogueSearchLoading}
              placeholder={loadingOptions ? 'Loading...' : strings.fields?.product_catalogue_id?.placeholder || 'Select product...'}
              error={errors.product_catalogue_id}
              onSearchChange={handleCatalogueSearch}
              options={productCatalogues.map(p => ({
                value: p.id,
                label: p.product_name || p.name,
              }))}
            />
            {errors.product_catalogue_id && (
              <p className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>
                {errors.product_catalogue_id}
              </p>
            )}
          </div>

          {/* Warehouse Inventory Section (Only for Add Mode) */}
          {/* Shown when editing too, not just creating. On an existing size this is how
              stock is added to a warehouse: coils entered here are credited onto that
              warehouse's balance. Editing a live balance is still not offered — that
              belongs to the warehouse-inventory screens. */}
          {(
            <div className="space-y-4 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
              <div className="flex items-center justify-between">
                <label
                  className="text-base font-bold"
                  style={{ color: UI_COLORS.TEXT.PRIMARY }}
                >
                  <FontAwesomeIcon icon={faWarehouse} className="mr-2" />
                  Warehouse Inventory
                </label>
                
                <div className="flex items-center gap-2 min-w-[200px]">
                  <SearchableSelect
                    options={availableWarehouses
                      .filter(hw => !formData.warehouses.some(w => String(w.warehouse_id) === String(hw.id)))
                      .map(hw => ({ value: hw.id, label: hw.name }))}
                    onChange={(e) => handleAddWarehouse(e.target.value)}
                    placeholder="Add warehouse..."
                    loading={loadingWarehouses}
                    value=""
                    className="w-full"
                  />
                </div>
              </div>

              <style>
                {`
                  .no-spinners::-webkit-outer-spin-button,
                  .no-spinners::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                  }
                  .no-spinners {
                    -moz-appearance: textfield;
                  }
                `}
              </style>

              {formData.warehouses.length > 0 ? (
                <div className={formData.coil_tracked ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                  {formData.warehouses.map((w, idx) => (
                    <div
                      key={w.warehouse_id}
                      className="p-4 rounded-xl border-2 transition-all duration-200 flex flex-col gap-3"
                      style={{
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
                        borderColor: (errors[`warehouses.${idx}.stock_quantity`] || errors[`warehouses.${idx}.coils`])
                          ? UI_COLORS.STATUS.ERROR
                          : UI_COLORS.BORDERS.LIGHT
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                          {w.warehouse_name || `Warehouse #${w.warehouse_id}`}
                        </span>
                        {/* No remove button on a warehouse the size is already stocked in:
                            the save is additive and the server would ignore the removal,
                            so the button would silently do nothing. Unlinking a warehouse
                            is done from the warehouse-inventory screens. */}
                        {!w.existing && (
                          <button
                            type="button"
                            onClick={() => handleRemoveWarehouse(w.warehouse_id)}
                            className="text-sm p-1.5 rounded-lg transition-colors hover:bg-red-50"
                            style={{ color: UI_COLORS.STATUS.ERROR }}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-semibold mb-1 block" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                          Stock Quantity
                        </label>
                        <input
                          type="number"
                          // Coil-tracked: the sum of the coils below, recomputed as they
                          // are entered. Never typed, so it cannot disagree with them.
                          //
                          // On a warehouse the size is ALREADY in, the coils entered here
                          // are ADDED to what is already there, so the figure shown is the
                          // resulting balance. Showing the new coils alone would read as
                          // though the existing stock had vanished.
                          value={
                            formData.coil_tracked
                              ? (w.existing
                                  ? (Number(w.stock_quantity) || 0) + coilWeightTotal(w)
                                  : coilWeightTotal(w))
                              : w.stock_quantity
                          }
                          onChange={(e) => handleWarehouseStockChange(w.warehouse_id, e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          // An existing balance is never retyped here — the API ignores
                          // stock_quantity for a warehouse already linked, so an editable
                          // box would accept a number that silently did nothing.
                          readOnly={!!formData.coil_tracked || !!w.existing}
                          disabled={!!formData.coil_tracked || !!w.existing}
                          className="w-full px-3 py-2 border-2 rounded-lg text-sm focus:ring-2 transition-all duration-200 no-spinners disabled:cursor-not-allowed disabled:opacity-60"
                          style={{
                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                            borderColor: errors[`warehouses.${idx}.stock_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                            color: UI_COLORS.FORMS.INPUT.text,
                          }}
                          placeholder="0"
                          min="0"
                        />
                        {/* Opening stock on a coil-tracked size is not a number the
                            operator withholds — it is the total of the coils below, so the
                            field says where its value comes from. */}
                        {formData.coil_tracked && (
                          <p className="mt-1 text-[10px] font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {t('coils.productSize.coilTracked.stockLocked')}
                          </p>
                        )}
                        {/* Say plainly that the coils below ADD to what is already here,
                            and what the balance was before this edit. */}
                        {w.existing && (
                          <p className="mt-1 text-[10px] font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
                            {formData.coil_tracked && coilWeightTotal(w) > 0
                              ? t('coils.productSize.existingWarehouse.adding', {
                                  current: formatKg(Number(w.stock_quantity) || 0),
                                  added: formatKg(coilWeightTotal(w)),
                                })
                              : t('coils.productSize.existingWarehouse.current', {
                                  current: formatKg(Number(w.stock_quantity) || 0),
                                })}
                          </p>
                        )}
                        {errors[`warehouses.${idx}.stock_quantity`] && (
                          <p className="mt-1 text-[10px] font-medium text-red-500">
                            {errors[`warehouses.${idx}.stock_quantity`]}
                          </p>
                        )}
                      </div>

                      {/* Opening coils for this warehouse. Each row is one physical roll
                          with its own kg/m — the number every metre figure downstream is
                          computed from, so it is on the card, not hidden behind a click.

                          Also gated on the GLOBAL switch, not just the per-size flag: a
                          size can still carry coil_tracked from when the feature was on,
                          and the server refuses new coils while the switch is off. Without
                          this the operator could key in a dozen coils and only learn at
                          save time that none of them could be accepted. */}
                      {coilTrackingEnabled && formData.coil_tracked && (
                        <div className="space-y-2 pt-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                              <FontAwesomeIcon icon={faLayerGroup} className="mr-1.5" />
                              {t('coils.productSize.openingCoils.label')}
                            </span>
                            {coilsFor(w).length > 0 && (
                              <span className="text-[11px] font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {t('coils.line.summary', {
                                  count: coilsFor(w).length,
                                  weight: formatKg(coilWeightTotal(w)),
                                })}
                              </span>
                            )}
                          </div>

                          {coilsFor(w).length === 0 ? (
                            <p className="text-[11px] italic" style={{ color: UI_COLORS.TEXT.MUTED }}>
                              {t('coils.productSize.openingCoils.none')}
                            </p>
                          ) : (
                            <ul className="space-y-1.5">
                              {coilsFor(w).map((coil, coilIdx) => {
                                // This coil's OWN ratio — coils of one size differ from
                                // each other, so it is never taken from the product master.
                                const perMetre = kgPerMetre({
                                  original_weight_kg: coil.weight_kg,
                                  original_length_m: coil.length_m,
                                });

                                return (
                                  <li
                                    key={`${w.warehouse_id}-${coil.batch_number}-${coilIdx}`}
                                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg"
                                    style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
                                  >
                                    <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                                      <span className="text-xs font-semibold truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                        {t('coils.productSize.openingCoils.row', {
                                          batch: coil.batch_number,
                                          weight: formatKg(coil.weight_kg),
                                          length: formatMetres(coil.length_m),
                                        })}
                                      </span>
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
                                        style={{
                                          backgroundColor: colorHelpers.withOpacity(
                                            perMetre > 0 ? UI_COLORS.STATUS.INFO : UI_COLORS.TEXT.MUTED, 0.12
                                          ),
                                          color: perMetre > 0 ? UI_COLORS.STATUS.INFO : UI_COLORS.TEXT.MUTED,
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faBalanceScale} className="text-[9px]" />
                                        {perMetre > 0
                                          ? t('coils.entry.computedKgPerMetre', { value: perMetre.toFixed(3) })
                                          : t('coils.entry.awaitingKgPerMetre')}
                                      </span>
                                      {coil.colour && (
                                        <span className="text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                          {coil.colour}
                                        </span>
                                      )}
                                      {coil.supplier_batch_ref && (
                                        <span className="text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                          {coil.supplier_batch_ref}
                                        </span>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveCoil(w.warehouse_id, coilIdx)}
                                      className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110 shrink-0"
                                      style={{
                                        color: UI_COLORS.STATUS.ERROR,
                                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                                      }}
                                      aria-label={t('coils.entry.removeRow')}
                                      title={t('coils.entry.removeRow')}
                                    >
                                      <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}

                          <button
                            type="button"
                            onClick={() => setCoilSheetWarehouseId(w.warehouse_id)}
                            className="w-full py-2 rounded-lg text-xs font-semibold border-2 border-dashed transition-all duration-200"
                            style={{ borderColor: UI_COLORS.BORDERS.MEDIUM, color: UI_COLORS.TEXT.SECONDARY }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                            {coilsFor(w).length > 0 ? t('coils.line.editCoils') : t('coils.receipt.enterCoils')}
                          </button>

                          {errors[`warehouses.${idx}.coils`] && (
                            <p className="text-[10px] font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>
                              {errors[`warehouses.${idx}.coils`]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div 
                  className="p-6 rounded-xl border-2 border-dashed text-center"
                  style={{ 
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.3),
                    borderColor: UI_COLORS.BORDERS.LIGHT 
                  }}
                >
                  <div className="text-gray-400 text-sm italic">
                    No warehouses selected. Use the dropdown above to add warehouses and their stock.
                  </div>
                </div>
              )}
            </div>
          )}

          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium mt-4"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          {/* Submit Buttons — key off the form's own `submitting` so they can't
              be left stuck by external state, and closing happens only on success. */}
          <div className="flex justify-end space-x-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                color: UI_COLORS.BUTTONS.SECONDARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}`
              }}
            >
              {strings.buttons?.cancel}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                border: `2px solid ${UI_COLORS.BUTTONS.PRIMARY.background}`
              }}
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {submitting
                ? (productSize ? strings.buttons?.submit?.loading?.edit : strings.buttons?.submit?.loading?.add)
                : (productSize ? strings.buttons?.submit?.edit : strings.buttons?.submit?.add)
              }
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* The same coil editor purchase unloading uses. It is a right-side drawer, so it
        slides in beside this modal instead of burying it, and it is offline: the rows come
        back through onSave and are held in form state until the whole product is saved in
        one request. Rendered OUTSIDE the <form> so its Enter-to-next-row keyboard flow can
        never submit the product size. */}
    {coilSheetWarehouse && (
      <CoilEntrySheet
        isOpen
        onClose={() => setCoilSheetWarehouseId(null)}
        initialCoils={coilsFor(coilSheetWarehouse)}
        productName={coilSheetTitle}
        // No expected quantity: opening stock is whatever is physically in the warehouse,
        // not a figure to reconcile against a purchase line.
        expectedQuantity={null}
        onSave={(coils) => handleCoilsSaved(coilSheetWarehouse.warehouse_id, coils)}
      />
    )}
    </>
  );
};

export default ProductSizeForm;
